import { zodResolver } from "@hookform/resolvers/zod";
import {
  type CountryISOCodeUpper,
  type KdsButtonProps,
  KdsDrawer,
} from "@kazaar-platform/design-system";
import { RiDeleteBinLine } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { HttpStatusCode, isAxiosError } from "axios";
import { useAtomValue } from "jotai";
import { parsePhoneNumber } from "libphonenumber-js";
import { isEmpty, isNil, omit } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  type AddressCreateCommand,
  type AddressReadDto,
  type AddressUpdateCommand,
} from "@/config/apiClient";
import { useAddress, useToasts } from "@/hooks";
import { userAtom } from "@/store";
import { AddressDeleteModal } from "./_partials/AddressDeleteModal";
import { AddressForm } from "./_partials/AddressForm";
import {
  type AddressFormSchema,
  addressFormSchema,
  getAddressFormDefaultValues,
} from "./_partials/AddressForm/AddressForm.schema";
import { AddressList } from "./_partials/AddressList";
import {
  type AddressDrawerProps,
  AddressDrawerTypeEnum,
} from "./AddressDrawer.types";

export const AddressDrawer = ({
  isOpen,
  addressToBeEdited,
  selectedAddresses: initialSelectedAddresses,
  onOpenChange,
  onDeleteAddressCallback,
  onSelectAddress,
}: AddressDrawerProps) => {
  // Hooks
  const { t } = useTranslate();
  const { add } = useToasts();
  const userAtomValue = useAtomValue(userAtom);

  // States
  const [drawerType, setDrawerType] = useState<AddressDrawerTypeEnum>(
    AddressDrawerTypeEnum.LIST
  );
  const [addressToEdit, setAddressToEdit] = useState<
    Omit<AddressReadDto, "createdAt"> | undefined
  >(undefined);
  const [selectedAddresses, setSelectedAddresses] = useState<AddressReadDto[]>(
    initialSelectedAddresses
  );
  const [isDeleteAddressModalOpen, setIsDeleteAddressModalOpen] =
    useState<boolean>(false);

  // Query hooks
  const { useAddAddress, useUpdateAddress } = useAddress();
  const { mutateAsync: addAddress, isPending: isAddingAddress } =
    useAddAddress();
  const { mutateAsync: updateAddress, isPending: isUpdatingAddress } =
    useUpdateAddress();

  // Form hook
  const methods = useForm<AddressFormSchema>({
    resolver: zodResolver(addressFormSchema),
    defaultValues: getAddressFormDefaultValues(userAtomValue?.lang, undefined),
  });

  // Set default values and states (when addressToBeEdited props is provided)
  useEffect(() => {
    if (addressToBeEdited) {
      setAddressToEdit(addressToBeEdited);
      setDrawerType(AddressDrawerTypeEnum.FORM);
      methods.reset(
        getAddressFormDefaultValues(userAtomValue?.lang, addressToBeEdited)
      );
    }
  }, [addressToBeEdited, methods.reset, userAtomValue?.lang]);

  // Check which mode we are in (add or modify)
  const isAddMode = useMemo(() => isNil(addressToEdit), [addressToEdit]);

  // Update selectedAddresses
  useEffect(() => {
    setSelectedAddresses(initialSelectedAddresses);
  }, [initialSelectedAddresses]);

  // Handle OnPress Edit Address
  const handleOnPressEditAddress = (address: AddressReadDto | undefined) => {
    setAddressToEdit(address);
    setDrawerType(AddressDrawerTypeEnum.FORM);
    methods.reset(getAddressFormDefaultValues(userAtomValue?.lang, address));
  };

  // Handle OnCancel Edit Address
  const handleOnCancel = () => {
    setAddressToEdit(undefined);
    setDrawerType(AddressDrawerTypeEnum.LIST);
  };

  // Handle Submit
  const handleSubmit = async (data: AddressFormSchema) => {
    const formatAddress = (
      address: AddressFormSchema
    ): Omit<AddressFormSchema, "phoneCountry"> => {
      if (
        !isNil(address.phoneNumber) &&
        !isEmpty(address.phoneNumber) &&
        !isNil(address.phoneCountry) &&
        !isEmpty(address.phoneCountry)
      ) {
        const country =
          address.phoneCountry.toUpperCase() as CountryISOCodeUpper;
        let phone = address.phoneNumber;

        try {
          const formattedPhoneNumber = parsePhoneNumber(phone, country);

          if (formattedPhoneNumber) {
            phone = formattedPhoneNumber.number;
          }
          return omit({ ...address, phoneNumber: phone }, "phoneCountry");
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error("Error while formatting the phone number", error);
          }
        }

        return omit(address, "phoneCountry");
      }

      return omit(address, "phoneCountry");
    };

    const formattedAddress = formatAddress(data);

    try {
      const response = isAddMode
        ? await addAddress(formattedAddress as AddressCreateCommand)
        : await updateAddress({
            ...formattedAddress,
            organisationId: addressToEdit?.organisationId,
          } as AddressUpdateCommand);

      if (response.data.id) {
        add({
          state: "success",
          title: isAddMode
            ? t("addressDrawer.addressForm.toasts.successAdd")
            : t("addressDrawer.addressForm.toasts.successEdit"),
        });

        // Uodate Selected Addresses in form (if needed)
        if (!isAddMode) {
          const updatedSelectedAddresses: AddressReadDto[] = [];
          selectedAddresses.forEach((selectedAddress) => {
            const isAddressToUpdate =
              selectedAddress.address === addressToEdit?.address &&
              selectedAddress.name === addressToEdit.name &&
              selectedAddress.recipientName === addressToEdit.recipientName;
            updatedSelectedAddresses.push(
              !isAddressToUpdate ? selectedAddress : response.data
            );
          });
          onSelectAddress(updatedSelectedAddresses);
        }

        handleOnCancel();
      }
    } catch (err) {
      if (isAxiosError(err) && err?.status === HttpStatusCode.Conflict) {
        add({
          title: t("addressDrawer.addressForm.toasts.errorDuplicate"),
          state: "error",
        });
      } else {
        handleSubmitError();
      }
    }
  };

  // Handle Submit Error
  const handleSubmitError = () => {
    add({
      state: "error",
      title: t("toast.generic.error"),
    });
  };

  // Form footer buttons
  const formFooterButtons: KdsButtonProps[] = [
    {
      children: t("buttons.cancel"),
      color: "neutral",
      variant: "outlined",
      onPress: handleOnCancel,
    },
    {
      children: isAddMode ? t("buttons.add") : t("buttons.update"),
      isLoading: isAddingAddress || isUpdatingAddress,
      dataCy: "addressDrawerAddUpdateButton",
      onPress: () =>
        void methods.handleSubmit(handleSubmit, handleSubmitError)(),
    },
  ];

  if (!isAddMode) {
    formFooterButtons.push({
      children: <RiDeleteBinLine />,
      color: "error",
      variant: "outlined",
      onPress: () => setIsDeleteAddressModalOpen(true),
    });
  }

  return (
    <FormProvider {...methods}>
      <KdsDrawer
        isOpen={isOpen}
        onOpenChange={() => {
          handleOnCancel();
          setSelectedAddresses(selectedAddresses);
          onOpenChange();
        }}
        dialogProps={{
          dialogHeaderProps: {
            title: t("addressDrawer.title"),
          },
          content: (
            <>
              {drawerType === AddressDrawerTypeEnum.LIST ? (
                <AddressList
                  selectedAddresses={selectedAddresses}
                  onPressEdit={(address: AddressReadDto | undefined) =>
                    handleOnPressEditAddress(address)
                  }
                  onSelect={(addresses: AddressReadDto[]) =>
                    setSelectedAddresses(addresses)
                  }
                />
              ) : (
                <AddressForm
                  addressToEdit={addressToEdit}
                  onCancel={handleOnCancel}
                />
              )}
            </>
          ),
          dialogFooterProps:
            drawerType === AddressDrawerTypeEnum.LIST
              ? {
                  fixedFooter: true,
                  type: "triple",
                  buttonProps: [
                    {
                      children: t("buttons.cancel"),
                      color: "neutral",
                      variant: "outlined",
                      onPress: () => {
                        setSelectedAddresses(selectedAddresses);
                        onOpenChange();
                      },
                    },
                    {
                      children: t("buttons.select"),
                      dataCy: "addressDrawerSelectButton",
                      onPress: () => {
                        onSelectAddress(selectedAddresses);
                        onOpenChange();
                      },
                    },
                  ],
                  subtitle: t("addressDrawer.nbOfSelectedAddresses", {
                    count: selectedAddresses.length,
                  }),
                }
              : {
                  fixedFooter: true,
                  type: "triple",
                  buttonProps: formFooterButtons,
                },
        }}
      />

      {/* Modal Delete Address */}
      {!isAddMode && !!addressToEdit?.id && (
        <AddressDeleteModal
          isOpen={isDeleteAddressModalOpen}
          addressId={addressToEdit.id}
          onDeleteCallback={() =>
            onDeleteAddressCallback
              ? onDeleteAddressCallback(addressToEdit)
              : undefined
          }
          onOpenChange={() => {
            setIsDeleteAddressModalOpen(false);
            handleOnCancel();
          }}
        />
      )}
    </FormProvider>
  );
};
